import { useEffect, useRef, useState } from "react"
import { useMaybeRoomContext } from "@livekit/components-react"
import { levelComesFromCall } from "../../../engine/precall/useMicrophoneLevel"

const SILENT_THRESHOLD_MS = 1500

/**
 * Detects OS-level mute (keyboard mute key, system mute):
 * when mic permissions are granted and mic is enabled, but audio level
 * stays at 0 for 1.5 seconds, we consider the mic "silent".
 *
 * @param {number} micLevel - Current mic level (0–1)
 * @param {boolean} micShouldWork - true when permissions OK, mic enabled, and sound mode active
 * @returns {boolean} isMicSilent
 */
export const useMicSilentDetection = (micLevel, rawMicShouldWork) => {
    // A zero from LiveKit means "not talking", not "muted": it can't tell an
    // OS mute from silence, so the detection only reads the app's own capture.
    const room = useMaybeRoomContext()
    const micShouldWork = rawMicShouldWork && !levelComesFromCall(room)
    const [isMicSilent, setIsMicSilent] = useState(false)
    const timerRef = useRef(null)

    useEffect(() => {
        if (micShouldWork && micLevel === 0) {
            if (!timerRef.current) {
                timerRef.current = setTimeout(() => {
                    timerRef.current = null
                    setIsMicSilent(true)
                }, SILENT_THRESHOLD_MS)
            }
        } else if (micLevel > 0 || !micShouldWork) {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
            setIsMicSilent(false)
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }
    }, [micLevel, micShouldWork])

    return isMicSilent
}
